// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/ui-core/transition.tsx
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/ui/src/transition.tsx
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo listto-app
//     node fleet/fleet.ts check --repo listto-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import {
  $TRACK,
  batch,
  children,
  createComputed,
  createMemo,
  createSignal,
  type JSX,
  untrack,
  useTransition,
} from "solid-js";
import { isServer } from "solid-js/web";

export interface TransitionEvents {
  onBeforeEnter?: (el: Element) => void;
  onEnter?: (el: Element, done: () => void) => void;
  onAfterEnter?: (el: Element) => void;
  onBeforeExit?: (el: Element) => void;
  onExit?: (el: Element, done: () => void) => void;
  onAfterExit?: (el: Element) => void;
}

export interface TransitionClassnames {
  enter: string[];
  enterActive: string[];
  enterTo: string[];
  exit: string[];
  exitActive: string[];
  exitTo: string[];
  move: string[];
}

export interface TransitionProps extends TransitionEvents {
  name?: string;
  enterActiveClass?: string;
  enterClass?: string;
  enterToClass?: string;
  exitActiveClass?: string;
  exitClass?: string;
  exitToClass?: string;
  moveClass?: string;
  mode?: "inout" | "outin";
  appear?: boolean;
  children?: JSX.Element;
}

export interface TransitionGroupProps extends TransitionProps {
  children?: JSX.Element;
}

function createClassnames(props: TransitionProps) {
  return createMemo<TransitionClassnames>(() => {
    const name = props.name ?? "s";
    return {
      enterActive: (props.enterActiveClass ?? `${name}-enter-active`).split(" "),
      enter: (props.enterClass ?? `${name}-enter`).split(" "),
      enterTo: (props.enterToClass ?? `${name}-enter-to`).split(" "),
      exitActive: (props.exitActiveClass ?? `${name}-exit-active`).split(" "),
      exit: (props.exitClass ?? `${name}-exit`).split(" "),
      exitTo: (props.exitToClass ?? `${name}-exit-to`).split(" "),
      move: (props.moveClass ?? `${name}-move`).split(" "),
    };
  });
}

function nextFrame(fn: () => void) {
  requestAnimationFrame(() => {
    // The first frame can fire after the document is gone (an unmounted test
    // environment), where scheduling the second one would throw.
    if (typeof requestAnimationFrame !== "function") {
      return;
    }
    requestAnimationFrame(fn);
  });
}

function enterTransition(
  classes: TransitionClassnames,
  events: TransitionEvents,
  el: Element,
  done?: () => void,
) {
  const { onBeforeEnter, onEnter, onAfterEnter } = events;
  onBeforeEnter?.(el);
  el.classList.add(...classes.enter, ...classes.enterActive);

  queueMicrotask(() => {
    if (!el.parentNode) {
      return done?.();
    }
    onEnter?.(el, () => endTransition());
  });

  nextFrame(() => {
    el.classList.remove(...classes.enter);
    el.classList.add(...classes.enterTo);
    if (!onEnter || onEnter.length < 2) {
      el.addEventListener("transitionend", endTransition);
      el.addEventListener("animationend", endTransition);
    }
  });

  function endTransition(e?: Event) {
    if (!e || e.target === el) {
      done?.();
      el.removeEventListener("transitionend", endTransition);
      el.removeEventListener("animationend", endTransition);
      el.classList.remove(...classes.enterActive, ...classes.enterTo);
      onAfterEnter?.(el);
    }
  }
}

function exitTransition(
  classes: TransitionClassnames,
  events: TransitionEvents,
  el: Element,
  done?: () => void,
) {
  const { onBeforeExit, onExit, onAfterExit } = events;
  if (!el.parentNode) {
    return done?.();
  }
  onBeforeExit?.(el);
  el.classList.add(...classes.exit, ...classes.exitActive);
  onExit?.(el, () => endTransition());

  nextFrame(() => {
    el.classList.remove(...classes.exit);
    el.classList.add(...classes.exitTo);
    if (!onExit || onExit.length < 2) {
      el.addEventListener("transitionend", endTransition);
      el.addEventListener("animationend", endTransition);
    }
  });

  function endTransition(e?: Event) {
    if (!e || e.target === el) {
      done?.();
      el.removeEventListener("transitionend", endTransition);
      el.removeEventListener("animationend", endTransition);
      el.classList.remove(...classes.exitActive, ...classes.exitTo);
      onAfterExit?.(el);
    }
  }
}

const isElement = isServer
  ? (item: unknown): item is Element => item != null && typeof item === "object" && "t" in item
  : (item: unknown): item is Element => item instanceof Element;

function createSwitchTransition(
  source: () => Element | null,
  options: {
    mode?: "inout" | "outin";
    appear?: boolean;
    onEnter: (el: Element, done: () => void) => void;
    onExit: (el: Element, done: () => void) => void;
  },
) {
  const initSource = untrack(source);
  const initReturned = initSource ? [initSource] : [];
  if (isServer) {
    return () => initReturned;
  }

  const { onEnter, onExit } = options;
  const [returned, setReturned] = createSignal<Element[]>(options.appear ? [] : initReturned);
  const [isTransitionPending] = useTransition();
  let next: Element | undefined;
  let isExiting = false;

  function runExit(el: Element | null, after?: () => void) {
    if (!el) {
      return after?.();
    }
    isExiting = true;
    onExit(el, () => {
      batch(() => {
        isExiting = false;
        setReturned((p) => p.filter((e) => e !== el));
        after?.();
      });
    });
  }

  function runEnter(after?: () => void) {
    const el = next;
    if (!el) {
      return after?.();
    }
    next = undefined;
    setReturned((p) => [el, ...p]);
    onEnter(el, after ?? (() => {}));
  }

  const trigger =
    options.mode === "outin"
      ? (prev: Element | null) => isExiting || runExit(prev, runEnter)
      : options.mode === "inout"
        ? (prev: Element | null) => runEnter(() => runExit(prev))
        : (prev: Element | null) => {
            runExit(prev);
            runEnter();
          };

  createComputed(
    (prev: Element | null) => {
      const el = source();
      if (untrack(isTransitionPending)) {
        isTransitionPending();
        return prev;
      }
      if (el !== prev) {
        next = el ?? undefined;
        batch(() => untrack(() => trigger(prev)));
      }
      return el;
    },
    options.appear ? null : initSource,
  );

  return returned;
}

function createListTransition(
  source: () => Element[],
  options: {
    appear?: boolean;
    onChange: (params: {
      list: Element[];
      added: Element[];
      removed: Element[];
      unchanged: Element[];
      finishRemoved: (els: Element[]) => void;
    }) => void;
  },
) {
  const initSource = untrack(source);
  if (isServer) {
    const copy = [...initSource];
    return () => copy;
  }

  const { onChange } = options;
  let prevSet = new Set(options.appear ? undefined : initSource);
  const exiting = new WeakSet<Element>();
  const [toRemove, setToRemove] = createSignal<Element[]>([], { equals: false });
  const [isTransitionPending] = useTransition();

  const finishRemoved = (els: Element[]) => {
    setToRemove((p) => {
      p.push(...els);
      return p;
    });
    for (const el of els) {
      exiting.delete(el);
    }
  };

  return createMemo<Element[]>(
    (prev) => {
      const elsToRemove = toRemove();
      const sourceList = source();
      // @ts-expect-error top-level store tracking
      sourceList[$TRACK];
      if (untrack(isTransitionPending)) {
        isTransitionPending();
        return prev;
      }
      if (elsToRemove.length > 0) {
        const next = prev.filter((e) => !elsToRemove.includes(e));
        elsToRemove.length = 0;
        onChange({ list: next, added: [], removed: [], unchanged: next, finishRemoved });
        return next;
      }
      return untrack(() => {
        const nextSet = new Set(sourceList);
        const next = [...sourceList];
        const added: Element[] = [];
        const removed: Element[] = [];
        const unchanged: Element[] = [];
        for (const el of sourceList) {
          (prevSet.has(el) ? unchanged : added).push(el);
        }
        let nothingChanged = added.length === 0;
        for (let i = 0; i < prev.length; i++) {
          const el = prev[i]!;
          if (!nextSet.has(el)) {
            if (!exiting.has(el)) {
              removed.push(el);
              exiting.add(el);
            }
            next.splice(i, 0, el);
          }
          if (nothingChanged && el !== next[i]) {
            nothingChanged = false;
          }
        }
        if (removed.length === 0 && nothingChanged) {
          return prev;
        }
        onChange({ list: next, added, removed, unchanged, finishRemoved });
        prevSet = nextSet;
        return next;
      });
    },
    options.appear ? [] : [...initSource],
  );
}

function isTransitionEvent(e: Event): e is TransitionEvent {
  return "propertyName" in e;
}

export function Transition(props: TransitionProps): JSX.Element {
  const classnames = createClassnames(props);
  const resolved = children(() => props.children);
  const source = createMemo(() => {
    const list = (resolved.toArray() as unknown[]).filter(isElement);
    return list[0] ?? null;
  });

  const rendered = createSwitchTransition(source, {
    mode: props.mode,
    appear: props.appear,
    onEnter(el, done) {
      enterTransition(classnames(), props, el, done);
    },
    onExit(el, done) {
      exitTransition(classnames(), props, el, done);
    },
  });

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return rendered as unknown as JSX.Element;
}

export function TransitionGroup(props: TransitionGroupProps): JSX.Element {
  const classnames = createClassnames(props);
  const resolved = children(() => props.children);
  const source = createMemo(() => (resolved.toArray() as unknown[]).filter(isElement));

  const rendered = createListTransition(source, {
    appear: props.appear,
    onChange({ added, removed, finishRemoved, list }) {
      const classes = classnames();
      for (const el of added) {
        enterTransition(classes, props, el);
      }
      const toMove: { el: HTMLElement | SVGElement; rect: DOMRect }[] = [];
      for (const el of list) {
        if (el.isConnected && (el instanceof HTMLElement || el instanceof SVGElement)) {
          toMove.push({ el, rect: el.getBoundingClientRect() });
        }
      }
      queueMicrotask(() => {
        const moved: (HTMLElement | SVGElement)[] = [];
        for (const { el, rect } of toMove) {
          if (el.isConnected) {
            const newRect = el.getBoundingClientRect();
            const dX = rect.left - newRect.left;
            const dY = rect.top - newRect.top;
            if (dX || dY) {
              el.style.transform = `translate(${dX}px, ${dY}px)`;
              el.style.transitionDuration = "0s";
              moved.push(el);
            }
          }
        }
        if (typeof document !== "undefined") {
          // force reflow
          void document.body.offsetHeight;
        }
        for (const el of moved) {
          el.classList.add(...classes.move);
          el.style.transform = "";
          el.style.transitionDuration = "";
          const endTransition = (e: Event) => {
            if (e.target === el || (isTransitionEvent(e) && e.propertyName.endsWith("transform"))) {
              el.removeEventListener("transitionend", endTransition);
              el.classList.remove(...classes.move);
            }
          };
          el.addEventListener("transitionend", endTransition);
        }
      });
      for (const el of removed) {
        exitTransition(classes, props, el, () => finishRemoved([el]));
      }
    },
  });

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return rendered as unknown as JSX.Element;
}
